/**
 * Open.Video Hook Library
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HOOK_LIBRARY_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Open.Video Hook Library | Your Smart Stack</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }
  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }
  .container { max-width: 680px; margin: 0 auto; }
  .header {
    text-align: center;
    margin-bottom: 40px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }
  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }
  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(26px, 5vw, 36px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }
  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 460px;
    margin: 0 auto;
  }
  .controls { display: flex; flex-direction: column; gap: 16px; margin-bottom: 28px; }
  .search-wrap { position: relative; }
  .search-input {
    width: 100%;
    padding: 12px 16px 12px 42px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    color: var(--espresso);
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 2px;
    outline: none;
    transition: border-color 0.2s ease;
  }
  .search-input::placeholder { color: oklch(0.88 0.012 85); }
  .search-input:focus { border-color: var(--terracotta); }
  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: oklch(0.88 0.012 85);
    font-size: 16px;
    pointer-events: none;
  }
  .filter-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .filter-pill {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 7px 16px;
    border: 1.5px solid var(--border);
    border-radius: 2px;
    background: var(--white);
    color: var(--muted);
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  .filter-pill:hover { border-color: var(--terracotta-mid); color: var(--espresso); background: var(--terracotta-light); }
  .filter-pill.active { background: var(--terracotta); border-color: var(--terracotta); color: var(--white); }
  .results-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .results-count { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; color: var(--muted); }
  .results-count span { color: var(--terracotta); font-weight: 600; }
  .copy-all-btn {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: var(--terracotta);
    background: none;
    border: 1px solid var(--terracotta-mid);
    border-radius: 2px;
    padding: 6px 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .copy-all-btn:hover { background: var(--terracotta-light); }
  .section-label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin: 28px 0 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border);
  }
  .section-label:first-of-type { margin-top: 0; }
  .hooks-grid { display: flex; flex-direction: column; gap: 8px; }
  .hook-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 16px 18px;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;
  }
  .hook-card:hover { border-color: var(--terracotta-mid); background: var(--terracotta-light); }
  .hook-card.copied { border-color: var(--terracotta); background: var(--terracotta-light); }
  .hook-text { font-size: 14px; line-height: 1.6; color: var(--body); flex: 1; transition: color 0.2s ease; }
  .hook-card:hover .hook-text { color: var(--espresso); }
  .copy-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border);
    border-radius: 2px;
    background: var(--white);
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 1px;
    color: var(--muted);
  }
  .hook-card:hover .copy-btn { border-color: var(--terracotta-mid); color: var(--terracotta); }
  .hook-card.copied .copy-btn { background: var(--terracotta); border-color: var(--terracotta); color: var(--white); }
  .toast {
    position: fixed;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%) translateY(20px);
    background: var(--espresso);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 500;
    padding: 10px 22px;
    border-radius: 2px;
    opacity: 0;
    transition: all 0.3s ease;
    pointer-events: none;
    white-space: nowrap;
    z-index: 100;
  }
  .toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
  .empty-state { display: none; text-align: center; padding: 48px 20px; color: var(--muted); }
  .empty-state.visible { display: block; }
  .empty-state p { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 14px; line-height: 1.6; }
  .footer { text-align: center; margin-top: 48px; padding-top: 28px; border-top: 1px solid var(--border); }
  .footer p { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; color: oklch(0.88 0.012 85); letter-spacing: 0.04em; }
  .footer a { color: var(--terracotta); text-decoration: none; }
  .hook-group.hidden { display: none; }
</style>
</head>
<body>
<div class="container">
  <div class="header">
    <p class="eyebrow">Resource Center: Swipe File</p>
    <h1>Open.Video<br>Hook Library</h1>
    <p>Filter by niche, search for what you need, and click any hook to copy it instantly.</p>
  </div>
  <div class="controls">
    <div class="search-wrap">
      <span class="search-icon">🔍</span>
      <input class="search-input" type="text" id="searchInput" placeholder="Search hooks..." oninput="filterHooks()">
    </div>
    <div class="filter-row">
      <button class="filter-pill active" onclick="setFilter('all', this)">All</button>
      <button class="filter-pill" onclick="setFilter('general', this)">General</button>
      <button class="filter-pill" onclick="setFilter('coaches', this)">Coaches</button>
      <button class="filter-pill" onclick="setFilter('smallbiz', this)">Small Business</button>
      <button class="filter-pill" onclick="setFilter('podcasters', this)">Podcasters</button>
      <button class="filter-pill" onclick="setFilter('tiktok', this)">TikTok / Reels</button>
      <button class="filter-pill" onclick="setFilter('cta', this)">CTAs</button>
    </div>
  </div>
  <div class="results-bar">
    <div class="results-count"><span id="hookCount">30</span> hooks</div>
    <button class="copy-all-btn" onclick="copyAll()">Copy all visible</button>
  </div>
  <div id="hooksContainer">
    <div class="hook-group" data-niche="general">
      <div class="section-label">General</div>
      <div class="hooks-grid">
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Stop building someone else's platform: here's why I own my video channel.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">What if YouTube shut down your account tomorrow? Here's my solution.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">I moved my entire video library off YouTube: here's what happened.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Privacy-first video hosting exists, and it's changing how I share content.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">No ads. No algorithm. No data harvesting. Just your videos, your way.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
      </div>
    </div>
    <div class="hook-group" data-niche="coaches">
      <div class="section-label">Coaches &amp; Course Creators</div>
      <div class="hooks-grid">
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">I used to send clients to YouTube. Now I send them to my own branded video hub.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">My coaching videos are finally in one place: no YouTube, no Vimeo, no distractions.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Coaches: your content deserves a home that looks as professional as your offer.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Stop letting YouTube recommend your competitors after your videos.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Every module, every bonus, every replay: all in one branded space my clients love.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
      </div>
    </div>
    <div class="hook-group" data-niche="smallbiz">
      <div class="section-label">Small Business Owners</div>
      <div class="hooks-grid">
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">My product demos, testimonials, and how-tos: all hosted on my own domain.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">I stopped giving YouTube free SEO from my business videos. Here's what I use instead.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Small business owners: your video content is an asset. Treat it like one.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Your competitors are renting audience. Here's how to own yours.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
      </div>
    </div>
    <div class="hook-group" data-niche="podcasters">
      <div class="section-label">Podcasters &amp; Speakers</div>
      <div class="hooks-grid">
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Podcasters: stop losing control to Spotify and YouTube. Host your own.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Every talk I've ever given is now in one hub: here's how I built it.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Think of this as your TEDx page, but fully branded to you.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">All my episodes, all my talks, all my interviews: one place, my domain.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">I stopped depending on podcast platforms to deliver my audience. Here's what I did instead.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
      </div>
    </div>
    <div class="hook-group" data-niche="tiktok">
      <div class="section-label">TikTok &amp; Reels (Quick Binge Hooks)</div>
      <div class="hooks-grid">
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">YouTube is rented land. Here's how I own my audience.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">SEO loves my videos because they live on my domain.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">This one shift gave me 10x discoverability.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">What's better than subscribers? Owned subscribers.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">My channel, my rules. That's the power of owning your video.</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
      </div>
    </div>
    <div class="hook-group" data-niche="cta">
      <div class="section-label">CTA Variations</div>
      <div class="hooks-grid">
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Watch all my videos on my official hub: no ads, no distractions → [channel link]</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">This isn't YouTube: it's MY video library, built for you. Start exploring here → [channel link]</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Every video, every playlist, all in one place → [channel link]</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Skip the algorithm. Watch direct → [channel link]</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
        <div class="hook-card" onclick="copyHook(this)"><div style="flex:1"><div class="hook-text">Binge-watch everything on my official hub → [channel link]</div></div><div class="copy-btn"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="5" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div></div>
      </div>
    </div>
  </div>
  <div class="empty-state" id="emptyState">
    <p>No hooks match your search.<br>Try a different keyword or clear the filter.</p>
  </div>
</div>
<div class="toast" id="toast">Copied to clipboard</div>
<script>
  let activeFilter = 'all';
  function setFilter(niche, btn) {
    activeFilter = niche;
    document.querySelectorAll('.filter-pill').forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    filterHooks();
  }
  function filterHooks() {
    const query = document.getElementById('searchInput').value.toLowerCase().trim();
    const groups = document.querySelectorAll('.hook-group');
    let totalVisible = 0;
    groups.forEach(group => {
      const groupNiche = group.dataset.niche;
      const nicheMatch = activeFilter === 'all' || groupNiche === activeFilter;
      if (!nicheMatch) { group.classList.add('hidden'); return; }
      group.classList.remove('hidden');
      const cards = group.querySelectorAll('.hook-card');
      let groupVisible = 0;
      cards.forEach(card => {
        const text = card.querySelector('.hook-text').textContent.toLowerCase();
        const textMatch = !query || text.includes(query);
        card.style.display = textMatch ? 'flex' : 'none';
        if (textMatch) groupVisible++;
      });
      group.style.display = groupVisible > 0 ? 'block' : 'none';
      totalVisible += groupVisible;
    });
    document.getElementById('hookCount').textContent = totalVisible;
    document.getElementById('emptyState').classList.toggle('visible', totalVisible === 0);
  }
  function copyHook(card) {
    const text = card.querySelector('.hook-text').textContent;
    navigator.clipboard.writeText(text).then(() => {
      card.classList.add('copied');
      setTimeout(() => card.classList.remove('copied'), 1800);
      showToast('Copied to clipboard');
    });
  }
  function copyAll() {
    const visibleTexts = [];
    document.querySelectorAll('.hook-card').forEach(card => {
      if (card.style.display !== 'none') {
        visibleTexts.push(card.querySelector('.hook-text').textContent);
      }
    });
    navigator.clipboard.writeText(visibleTexts.join('\\n\\n')).then(() => {
      showToast(visibleTexts.length + ' hooks copied');
    });
  }
  function showToast(msg) {
    const toast = document.getElementById('toast');
    toast.textContent = msg;
    toast.classList.add('show');
    setTimeout(() => toast.classList.remove('show'), 2200);
  }
<\/script>
</body>
</html>`;

export default function OpenVideoHooks() {
  return (
    <GuideShell
      context={"Open.Video guide"}
      backHref={"/resources/open-video"}
      backLabel={"Back to Open.Video"}
    >
      <GuideFrame html={HOOK_LIBRARY_HTML} title={"Open.Video Hook Library"} />
    </GuideShell>
  );
}
